import { useCallback } from 'react';
import useSWR from 'swr';
import { Report } from '../types';
import { apiClient } from '../services/apiClient';

interface UseReportReturn {
  report: Report | null;
  loading: boolean;
  error: Error | undefined;
  refresh: () => Promise<void>;
  updateCache: (report: Report) => Promise<void>;
}

// Fetcher function for SWR
const reportFetcher = async (reportId: string): Promise<Report | null> => {
  const result = await apiClient.getReportById(reportId);
  return result ?? null;
};

export const useReport = (reportId: string | undefined): UseReportReturn => {
  const { data, error, isLoading, mutate } = useSWR<Report | null, Error>(
    reportId ? `/api/v1/reports/${reportId}` : null,
    () => reportFetcher(reportId!),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
    }
  );

  const report = data ?? null;
  const loading = isLoading;

  const refresh = useCallback(async (): Promise<void> => {
    await mutate();
  }, [mutate]);

  const updateCache = useCallback(async (updatedReport: Report): Promise<void> => {
    await mutate(updatedReport, { revalidate: false });
  }, [mutate]);

  return {
    report,
    loading,
    error,
    refresh,
    updateCache,
  };
};
